import type { DeliveryArea } from "../services/deliveryAreaService";

/**
 * Delivery charges from the delivery areas (Settings → Delivery areas):
 * each area has a flat charge, waived once the items total reaches its
 * "free above" amount — the same rule the website's checkout uses.
 */

type AreaPrice = Pick<DeliveryArea, "delivery_charge" | "free_above_amount">;

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Whether the area delivers free at this items total. */
export function isFreeDelivery(area: AreaPrice, itemsTotal: number): boolean {
    return area.free_above_amount != null && itemsTotal >= Number(area.free_above_amount);
}

/** What the customer pays for delivery to this area at this items total. */
export function areaCharge(area: AreaPrice, itemsTotal: number): number {
    return isFreeDelivery(area, itemsTotal) ? 0 : round2(Number(area.delivery_charge));
}

/**
 * The charge after the items total moves from `fromTotal` to `toTotal` — or
 * null to leave it. A charge still on the area's automatic amount follows
 * it (e.g. turns free past the threshold); one staff typed in stays.
 */
export function rechargeForTotal(
    area: AreaPrice,
    currentCharge: number,
    fromTotal: number,
    toTotal: number,
): number | null {
    if (round2(Number(currentCharge)) !== areaCharge(area, fromTotal)) return null;
    const next = areaCharge(area, toTotal);
    return next === round2(Number(currentCharge)) ? null : next;
}
